import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './AprobadoresModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Aprobadores";

const endpoint = ENDPOINTS.aprobadores;
const endpointById = ENDPOINTS.aprobadoresById;
const modelo = Models.aprobadores;

// 🔗 Endpoints extras para enriquecer con nombre de persona
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

// 🔗 Endpoint + modelo para TIPOS DE APROBACIÓN
const endpointTipoAprobacion = ENDPOINTS.tipoAprobacion;
const modeloTipoAprobacion = Models.tipoAprobacion;

const maps = {
  mapCreate: (f) => ({
    idEmpleado: Number(f.idEmpleado) || 0,
    idTipoAprobacion: Number(f.idTipoAprobacion) || 0,
    activo: !!f.activo,
    recibeNotificaciones: f.recibeNotificaciones ?? true,
  }),
  mapUpdate: (id, f) => ({
    idAprobadores: id,
    idEmpleado: Number(f.idEmpleado) || 0,
    idTipoAprobacion: Number(f.idTipoAprobacion) || 0,
    activo: !!f.activo,
    recibeNotificaciones: f.recibeNotificaciones ?? true,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('Aprobadores');

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 👇 Traemos todo de un jalón
      const [resAprob, resEmps, resPers, resTipos] = await Promise.all([
        axios.get(endpoint),
        axios.get(endpointEmpleado),
        axios.get(endpointPersona),
        axios.get(endpointTipoAprobacion),
      ]);

      let aprobNorm = (resAprob.data || []).map((x) => modelo(x));
      const empsNorm = (resEmps.data || []).map((x) => modeloEmpleado(x));
      const persNorm = (resPers.data || []).map((x) => modeloPersona(x));
      const tiposNorm = (resTipos.data || []).map((x) => modeloTipoAprobacion(x));

      const isSuperAdmin = isSessionSuperAdmin();
      const sessionEmpresaId = getSessionEmpresaId();

      // Enriquecer aprobadores con:
      // - nombreEmpleado
      // - numeroEmpleado
      // - nombreTipoAprobacion
      // - idEmpresa (desde el registro)
      // - nombreEmpresa (desde el registro)
      aprobNorm = aprobNorm.map(ap => {
        const emp = empsNorm.find(e => e.idEmpleado === ap.idEmpleado);
        const per = emp
          ? persNorm.find(p => p.idPersona === emp.idPersona)
          : null;

        const fullName = per
          ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
          : '';

        const tipo = tiposNorm.find(t => t.idTipoAprobacion === ap.idTipoAprobacion);

        return {
          ...ap,
          nombreEmpleado: fullName || `Empleado #${ap.idEmpleado}`,
          numeroEmpleado: emp?.numeroEmpleado ?? '',
          nombreTipoAprobacion: tipo?.nombre || `Tipo #${ap.idTipoAprobacion}`,
        };
      });

      if (!isSuperAdmin) {
        aprobNorm = aprobNorm.filter(ap => 
          ap.idEmpresa == sessionEmpresaId || 
          ap.idEmpresa === null || 
          ap.idEmpresa === undefined
        );
      }

      setRows(aprobNorm);
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // 🔹 Primero filtramos
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const empId = String(r.idEmpleado ?? '').toLowerCase();
      const empNo = String(r.numeroEmpleado ?? '').toLowerCase();
      const empNom = (r.nombreEmpleado || '').toLowerCase();
      const tipoId = String(r.idTipoAprobacion ?? '').toLowerCase();
      const tipoNom = (r.nombreTipoAprobacion || '').toLowerCase();
      const empName = (r.nombreEmpresa || '').toLowerCase();
      const activo = r.activo ? 'si' : 'no';

      return (
        empId.includes(q) ||
        empNo.includes(q) ||
        empNom.includes(q) ||
        tipoId.includes(q) ||
        tipoNom.includes(q) ||
        empName.includes(q) ||
        activo.includes(q)
      );
    });
  }, [rows, query]);

  // 🔹 Luego agrupamos por empleado
  const grouped = useMemo(() => {
    const map = new Map();

    filtered.forEach((r) => {
      const key = `${r.idEmpleado}-${r.numeroEmpleado}`;

      if (!map.has(key)) {
        map.set(key, {
          idEmpleado: r.idEmpleado,
          numeroEmpleado: r.numeroEmpleado,
          nombreEmpleado: r.nombreEmpleado,
          idEmpresa: r.idEmpresa,
          nombreEmpresa: r.nombreEmpresa,
          recibeNotificaciones: r.recibeNotificaciones,
          tiposDeAprobacion: new Set(),
          registros: [] // Guardamos todos los registros para poder editarlos
        });
      }

      const group = map.get(key);
      group.tiposDeAprobacion.add(r.idTipoAprobacion);
      group.registros.push(r);
    });

    // Convertimos el Map a array
    return Array.from(map.values()).map(g => ({
      ...g,
      cantidadTipos: g.tiposDeAprobacion.size,
      // Usamos el primer registro para mantener compatibilidad con edición/borrado
      primerRegistro: g.registros[0]
    }));
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(grouped.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return grouped.slice(start, start + PAGE_SIZE);
  }, [grouped, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idAprobadores === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    // Si el modal nos dice que solo refresquemos (porque ya guardó internamente)
    if (mode === 'refresh') {
      setModalOpen(false);
      await loadData();
      return;
    }

    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idAprobadores ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotificaciones = async (grupo) => {
    const nuevoEstado = !grupo.recibeNotificaciones;
    // Actualizamos todos los registros del empleado en paralelo
    try {
      setLoading(true);
      await Promise.all(
        grupo.registros.map(r =>
          axios.put(endpoint, maps.mapUpdate(r.idAprobadores, {
            ...r,
            recibeNotificaciones: nuevoEstado
          }))
        )
      );
      await loadData();
    } catch (e) {
      console.error('Error al cambiar estado de notificaciones:', e);
      alert('No se pudo cambiar el estado de las notificaciones.');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = grouped.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, grouped.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por empleado, no. empleado, tipo de aprobación o activo…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                   <th>No. Empleado</th>
                  <th style={{ textAlign: 'center' }}>Notificaciones</th>
                  <th>Empresa</th>
                  <th>Cantidad de tipos de aprobacion</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((g) => (
                  <tr key={`${g.idEmpleado}-${g.numeroEmpleado}`}>
                     <td>{normalizeValue(g.nombreEmpleado)}</td>
                    <td>{normalizeValue(g.numeroEmpleado)}</td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div
                        onClick={() => !loading && handleToggleNotificaciones(g)}
                        style={{
                          width: '38px',
                          height: '20px',
                          backgroundColor: g.recibeNotificaciones ? '#10b981' : '#cbd5e1',
                          borderRadius: '12px',
                          position: 'relative',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s',
                          flexShrink: 0,
                          opacity: loading ? 0.6 : 1,
                          margin: '0 auto'
                        }}
                        title={g.recibeNotificaciones ? "Desactivar notificaciones" : "Activar notificaciones"}
                      >
                        <div style={{
                          width: '14px',
                          height: '14px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          position: 'absolute',
                          top: '3px',
                          left: g.recibeNotificaciones ? '21px' : '3px',
                          transition: 'left 0.2s',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }} />
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.85rem', color: g.idEmpresa ? '#334155' : '#94a3b8' }}>
                        {g.nombreEmpresa || 'Global'}
                      </span>
                    </td>
                    <td>{g.cantidadTipos}</td>

                    {/* 🔽 Botón EDITAR con ícono */}
                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(g.primerRegistro.idAprobadores)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}

                    {/* 🔽 Botón BORRAR con ícono */}
                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            setEditingRowId(null);
                            const name = g.nombreEmpleado
                              ? `${g.nombreEmpleado} (${g.cantidadTipos} tipo${g.cantidadTipos > 1 ? 's' : ''})`
                              : `Empleado ${g.idEmpleado}`;
                            askDelete(g.primerRegistro.idAprobadores, name);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={5 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {grouped.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {grouped.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
